import { Phone, MapPin, ArrowRight, Home as HomeIcon, ChevronRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import BackToTop from "../components/BackToTop";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { SITE } from "../lib/site";
import PestCityIndex from "../components/PestCityIndex";
import CreuseMap from "../components/CreuseMap";
import { useCommunes } from "../lib/useCommunes";

const SLUG = "zone-intervention-creuse-et-limitrophes";
const CANONICAL = `https://kreuse-exterieur.fr/${SLUG}`;

// Adjacent departments to Creuse (23) — the actual French administrative neighbours.
const DEPARTMENTS = [
  {
    code: "23",
    name: "Creuse",
    color: "#1B5E20",
    badge: "Cœur d'activité",
    cities: ["Guéret", "La Souterraine", "Aubusson", "Bourganeuf", "Boussac", "La Celle-Dunoise", "Bussière-Dunoise"],
  },
  {
    code: "87",
    name: "Haute-Vienne",
    color: "#2E7D32",
    badge: "À l'ouest",
    cities: [],
  },
  {
    code: "36",
    name: "Indre",
    color: "#388E3C",
    badge: "Au nord-ouest",
    cities: [],
  },
  {
    code: "03",
    name: "Allier",
    color: "#43A047",
    badge: "Au nord-est",
    cities: [],
  },
];

const ZONES_SERVED = DEPARTMENTS.flatMap((d) =>
  d.cities.map((c) => `${c} (${d.code})`),
);

export default function ZoneInterventionPage() {
  const communes = useCommunes({ includeHidden: true });
  const hiddenNames = new Set(communes.filter((c) => c.hidden).map((c) => c.name));
  const addedNames = communes.filter((c) => !c.builtin && !c.hidden).map((c) => c.name);
  const citiesOf = (d) => (d.code === "23" ? [...d.cities.filter((n) => !hiddenNames.has(n)), ...addedNames] : d.cities);
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://kreuse-exterieur.fr/#business",
        name: "K'REUSE EXTÉRIEUR",
        description:
          "Paysagiste et lutte anti-nuisibles intervenant en Creuse (23) et dans les départements limitrophes : Haute-Vienne (87), Indre (36), Allier (03).",
        telephone: "+33780541115",
        url: CANONICAL,
        image: SITE.logo,
        address: {
          "@type": "PostalAddress",
          streetAddress: "27 rue des Pradelles",
          addressLocality: "La Celle-Dunoise",
          postalCode: "23800",
          addressRegion: "Creuse",
          addressCountry: "FR",
        },
        areaServed: DEPARTMENTS.map((d) => ({
          "@type": "AdministrativeArea",
          name: `${d.name} (${d.code})`,
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
          { "@type": "ListItem", position: 2, name: "Zone d'intervention", item: CANONICAL },
        ],
      },
    ],
  };

  useSEO({
    title:
      "Zone d'intervention : Creuse (23) et limitrophes | K'REUSE",
    description:
      "Paysagiste, élagage et anti-nuisibles en Creuse (23) et départements voisins : Haute-Vienne (87), Indre (36), Allier (03). Devis gratuit.",
    canonical: CANONICAL,
    jsonLd,
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-40 blur-3xl" />
            <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#E8F5E9] opacity-70 blur-3xl" />
          </div>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#5C705E] mb-6 flex-wrap">
              <Link to="/" className="flex items-center gap-1 hover:text-[#1B5E20]">
                <HomeIcon className="w-3.5 h-3.5" />
                Accueil
              </Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-[#1B5E20] font-medium">Zone d'intervention</span>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5EBE5] shadow-sm mb-6">
              <MapPin className="w-4 h-4 text-[#1B5E20]" />
              <span className="text-sm font-medium text-[#1A2E1C]">
                Creuse (23) + 3 départements limitrophes
              </span>
            </div>

            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A2E1C] leading-[1.05]">
              Paysagiste & anti-nuisibles{" "}
              <span className="font-medium text-[#1B5E20]">en Creuse et alentours</span>
            </h1>

            <p className="mt-6 text-lg text-[#5C705E] leading-relaxed max-w-3xl">
              Basés à La Celle-Dunoise au cœur de la Creuse, nous intervenons dans
              tout le département (23) ainsi que dans les communes limitrophes
              de la <strong>Haute-Vienne (87)</strong>, de l'<strong>Indre (36)</strong>{" "}
              et de l'<strong>Allier (03)</strong>. Un seul interlocuteur pour
              tous vos travaux d'extérieur.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                data-testid="zone-cta-call"
                className="bg-[#1B5E20] hover:bg-[#164d1a] text-white rounded-full px-7 py-6 text-base font-medium"
              >
                <a href={`tel:${SITE.phoneRaw}`}>
                  <Phone className="mr-2 w-4 h-4" />
                  {SITE.phone}
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="zone-cta-quote"
                className="bg-white border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-7 py-6 text-base font-medium"
              >
                <a href="/#contact">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Department grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                4 départements couverts
              </div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight">
                Où nous <span className="font-medium text-[#1B5E20]">intervenons</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {DEPARTMENTS.map((d) => (
                <div
                  key={d.code}
                  data-testid={`zone-dept-${d.code}`}
                  className="bg-white rounded-3xl p-6 border border-[#E5EBE5] hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center font-['Outfit'] font-bold text-2xl text-white"
                      style={{ backgroundColor: d.color }}
                    >
                      {d.code}
                    </div>
                    <span className="text-xs uppercase tracking-wider text-[#5C705E] font-semibold px-2.5 py-1 rounded-full bg-[#F1F8F2]">
                      {d.badge}
                    </span>
                  </div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#1A2E1C]">
                    {d.name}{" "}
                    <span className="text-[#5C705E] text-sm font-normal">({d.code})</span>
                  </h3>
                  <p className="text-sm text-[#5C705E] mt-1 mb-4">
                    {d.cities.length > 0
                      ? "Toute la Creuse est desservie, notamment :"
                      : "Interventions possibles dans tout le département, sur demande."}
                  </p>
                  <ul className="space-y-1.5">
                    {citiesOf(d).map((c) => (
                      <li
                        key={c}
                        className="flex items-center gap-2 text-sm text-[#1A2E1C]"
                      >
                        <Check className="w-3.5 h-3.5 text-[#1B5E20] flex-shrink-0" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services available everywhere */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-[#E8F5E9]/50 to-transparent">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-5 gap-10 items-start">
              <div className="lg:col-span-2">
                <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                  Mêmes services partout
                </div>
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight">
                  Quel que soit votre <span className="font-medium text-[#1B5E20]">département</span>
                </h2>
                <p className="text-[#5C705E] mt-5 leading-relaxed">
                  Que vous soyez en Creuse, en Haute-Vienne, dans l'Indre ou dans
                  l'Allier, vous bénéficiez du même savoir-faire, des mêmes
                  tarifs, et de la <strong>même réactivité</strong> (réponse sous
                  24h en moyenne).
                </p>
              </div>

              <ul className="lg:col-span-3 grid sm:grid-cols-2 gap-3">
                {[
                  { label: "Création de jardin sur-mesure", to: "/paysagiste-creuse" },
                  { label: "Entretien régulier (crédit d'impôt 50%)", to: "/entretien-jardin-creuse" },
                  { label: "Tonte de pelouse & taille de haies", to: "/entretien-jardin-creuse" },
                  { label: "Élagage et abattage d'arbres", to: "/elagage-creuse" },
                  { label: "Destruction de nids de frelons (asiatiques inclus)", to: "/destruction-frelons-creuse" },
                ].map((s) => (
                  <li key={s.to + s.label}>
                    <Link
                      to={s.to}
                      data-testid={`zone-service-${s.to.replace(/\//g, "")}`}
                      className="flex items-start gap-3 p-4 bg-white border border-[#E5EBE5] rounded-2xl hover:border-[#1B5E20] hover:shadow-sm transition-all"
                    >
                      <div className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-[#1B5E20]" />
                      </div>
                      <span className="text-sm font-medium text-[#1A2E1C] flex-1">
                        {s.label}
                      </span>
                      <ArrowRight className="w-4 h-4 text-[#5C705E] mt-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Long-tail SEO content (cities list) — boosts long-tail search coverage */}
        <section className="py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light text-[#1A2E1C] tracking-tight mb-6">
              Toutes les communes <span className="font-medium text-[#1B5E20]">desservies</span>
            </h2>
            <p className="text-[#5C705E] mb-8 max-w-3xl">
              Voici un aperçu non exhaustif des communes où nous intervenons
              régulièrement. Votre commune n'est pas dans la liste ?{" "}
              <a href="/#contact" className="text-[#1B5E20] underline underline-offset-2">
                Contactez-nous
              </a>{" "}
              — nous étudions chaque demande.
            </p>
            <div className="flex flex-wrap gap-2">
              {ZONES_SERVED.map((z) => (
                <span
                  key={z}
                  className="px-3 py-1.5 rounded-full bg-white border border-[#E5EBE5] text-[#1A2E1C] text-sm"
                >
                  {z}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#1B5E20] to-[#2e7d32] text-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light tracking-tight">
              Votre commune n'est pas dans la liste&nbsp;?
            </h2>
            <p className="mt-4 text-white/85">
              Tant que c'est dans un rayon raisonnable autour de la Creuse, nous
              étudions volontiers votre demande. Décrivez votre besoin et l'adresse —
              réponse sous 24h.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Button asChild className="bg-white text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-6 py-6 font-medium">
                <a href={`tel:${SITE.phoneRaw}`}>
                  <Phone className="mr-2 w-4 h-4" />
                  {SITE.phone}
                </a>
              </Button>
              <Button asChild className="bg-[#0F2E11] hover:bg-black text-white rounded-full px-6 py-6 font-medium">
                <a href="/#contact">
                  Demander un devis
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>
        <CreuseMap />
        <PestCityIndex />
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}
